import type {
  AnnotationDoorTarget,
  AnnotationInteraction,
  AnnotationInteractionEmoteEffect,
  AnnotationInteractionEffectPayload,
  AnnotationTriggerAnimationEffect,
  AnnotationTriggerEffect,
  AnnotationTriggerEmoteEffect,
  AnnotationTriggerEvent,
  AnnotationTriggerEventEffect,
  AnnotationTriggerSoundEffect,
  AnnotationTriggerStateChangeEffect,
  AnnotationStateRecord,
  AnnotationStateValue,
} from '../types/worldAnnotation';

export type AnnotationRuntimeStateById = Record<string, AnnotationStateRecord>;

export const MAX_STATE_ENTRIES = 24;
export const MAX_INTERACTIONS = 12;
export const MAX_TRIGGERS = 16;
export const MAX_TRIGGER_COOLDOWN_MS = 60 * 60 * 1000;
export const DEFAULT_TRIGGER_EMOTE_DURATION_MS = 1_500;
export const DEFAULT_TRIGGER_EFFECT_DURATION_MS = 1_200;
export const AUTHORED_STATE_SYMBOL = Symbol('authoredAnnotationState');

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const toStateValue = (value: unknown): AnnotationStateValue | undefined => {
  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'boolean' ||
    isFiniteNumber(value)
  ) {
    return value;
  }
  return undefined;
};

export const sanitizeToken = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

export const normalizeActionToken = (value: unknown, fallback: string): string => {
  const normalized = sanitizeToken(value)
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return normalized || fallback;
};

const normalizeTextPayload = (value: unknown): string | undefined => {
  const text = sanitizeToken(value);
  return text ? text : undefined;
};

export const normalizeTriggerEvent = (value: unknown): AnnotationTriggerEvent | undefined =>
  value === 'proximity_enter' ||
  value === 'proximity_exit' ||
  value === 'interact' ||
  value === 'state_change' ||
  value === 'event'
    ? value
    : undefined;

export const getAllowedTriggerEffectTypes = (
  event: AnnotationTriggerEvent,
): AnnotationTriggerEffect['type'][] => {
  if (event === 'proximity_enter' || event === 'proximity_exit') {
    return ['animation', 'sound', 'emote', 'event'];
  }
  return ['animation', 'sound', 'emote', 'event', 'state_change'];
};

export const normalizeEventName = (value: unknown): string | undefined => {
  const name = sanitizeToken(value);
  return name ? name : undefined;
};

export const isTriggerEffectCompatible = (
  event: AnnotationTriggerEvent,
  effectType: AnnotationTriggerEffect['type'],
): boolean => getAllowedTriggerEffectTypes(event).includes(effectType);

const normalizeDoorTargetPayload = (value: unknown): AnnotationDoorTarget | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const entry = value as Record<string, unknown>;
  const tileX = Number(entry.tileX);
  const tileY = Number(entry.tileY);
  const zone = sanitizeToken(entry.zone) || 'default';
  if (!Number.isFinite(tileX) || !Number.isFinite(tileY)) {
    return undefined;
  }
  return {
    tileX: Math.max(0, Math.floor(tileX)),
    tileY: Math.max(0, Math.floor(tileY)),
    zone,
  };
};

const normalizeEmotePayload = (value: unknown): AnnotationInteractionEmoteEffect | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const entry = value as Record<string, unknown>;
  const emote = sanitizeToken(entry.emote).toLowerCase();
  if (!emote) {
    return undefined;
  }
  const duration = Number(entry.duration);
  return {
    emote,
    ...(Number.isFinite(duration)
      ? { duration: Math.max(1, Math.min(10, Math.floor(duration))) }
      : {}),
  };
};

const normalizeTriggerAnimationEffect = (
  value: unknown,
): AnnotationTriggerAnimationEffect | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const entry = value as Record<string, unknown>;
  const variant = entry.variant === 'highlight' ? 'highlight' : 'pulse';
  const color = sanitizeToken(entry.color);
  const durationMs = Number(entry.durationMs);
  return {
    type: 'animation',
    payload: {
      variant,
      ...(color ? { color } : {}),
      ...(Number.isFinite(durationMs)
        ? {
            durationMs: Math.max(100, Math.min(MAX_TRIGGER_COOLDOWN_MS, Math.floor(durationMs))),
          }
        : { durationMs: DEFAULT_TRIGGER_EFFECT_DURATION_MS }),
      ...(typeof entry.loop === 'boolean' ? { loop: entry.loop } : {}),
    },
  };
};

const normalizeTriggerSoundEffect = (value: unknown): AnnotationTriggerSoundEffect | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const entry = value as Record<string, unknown>;
  const kind = entry.kind === 'noise' ? 'noise' : 'tone';
  const waveform =
    entry.waveform === 'triangle' || entry.waveform === 'square' || entry.waveform === 'sawtooth'
      ? entry.waveform
      : 'sine';
  const frequency = Number(entry.frequency);
  const gain = Number(entry.gain);
  const durationMs = Number(entry.durationMs);

  return {
    type: 'sound',
    payload: {
      kind,
      waveform,
      ...(Number.isFinite(frequency)
        ? { frequency: Math.max(40, Math.min(2_000, Math.floor(frequency))) }
        : {}),
      ...(Number.isFinite(gain) ? { gain: Math.max(0, Math.min(1, gain)) } : {}),
      ...(Number.isFinite(durationMs)
        ? {
            durationMs: Math.max(100, Math.min(MAX_TRIGGER_COOLDOWN_MS, Math.floor(durationMs))),
          }
        : { durationMs: DEFAULT_TRIGGER_EFFECT_DURATION_MS }),
      ...(typeof entry.loop === 'boolean' ? { loop: entry.loop } : {}),
    },
  };
};

const normalizeTriggerEmoteEffect = (value: unknown): AnnotationTriggerEmoteEffect | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const entry = value as Record<string, unknown>;
  const emote = sanitizeToken(entry.emote).toLowerCase();
  if (!emote) {
    return undefined;
  }
  const durationMs = Number(entry.durationMs);
  return {
    type: 'emote',
    payload: {
      emote,
      ...(Number.isFinite(durationMs)
        ? {
            durationMs: Math.max(100, Math.min(MAX_TRIGGER_COOLDOWN_MS, Math.floor(durationMs))),
          }
        : { durationMs: DEFAULT_TRIGGER_EMOTE_DURATION_MS }),
      ...(typeof entry.loop === 'boolean' ? { loop: entry.loop } : {}),
    },
  };
};

const normalizeTriggerEventEffect = (value: unknown): AnnotationTriggerEventEffect | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const entry = value as Record<string, unknown>;
  const name = sanitizeToken(entry.name);
  if (!name) {
    return undefined;
  }
  return {
    type: 'event',
    payload: {
      name,
      ...(normalizeAnnotationStateRecord(entry.detail)
        ? { detail: normalizeAnnotationStateRecord(entry.detail) }
        : {}),
    },
  };
};

const normalizeTriggerStateChangeEffect = (
  value: unknown,
): AnnotationTriggerStateChangeEffect | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const entry = value as Record<string, unknown>;
  const state = normalizeAnnotationStateRecord(entry.state);
  if (!state) {
    return undefined;
  }
  return {
    type: 'state_change',
    payload: {
      state,
    },
  };
};

export const normalizeAnnotationStateRecord = (
  value: unknown,
): AnnotationStateRecord | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const entries = Object.entries(value as Record<string, unknown>)
    .map(([key, entry]) => [sanitizeToken(key), toStateValue(entry)] as const)
    .filter(
      (entry): entry is [string, AnnotationStateValue] =>
        Boolean(entry[0]) && entry[1] !== undefined,
    )
    .slice(0, MAX_STATE_ENTRIES);

  if (entries.length === 0) {
    return undefined;
  }

  return Object.fromEntries(entries);
};

export const normalizeAnnotationRuntimeStateById = (
  value: unknown,
): AnnotationRuntimeStateById | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  const entries = Object.entries(value as Record<string, unknown>)
    .map(
      ([annotationId, record]) =>
        [sanitizeToken(annotationId), normalizeAnnotationStateRecord(record)] as const,
    )
    .filter(
      (entry): entry is [string, AnnotationStateRecord] => Boolean(entry[0]) && Boolean(entry[1]),
    );

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};

export const normalizeInteractionPayload = (
  effectType: AnnotationInteraction['effectType'],
  value: unknown,
): AnnotationInteractionEffectPayload | undefined => {
  if (effectType === 'text') {
    return normalizeTextPayload(value);
  }
  if (effectType === 'state_change') {
    return normalizeTextPayload(value);
  }
  if (effectType === 'navigate') {
    return normalizeDoorTargetPayload(value);
  }
  if (effectType === 'emote') {
    return normalizeEmotePayload(value);
  }
  return undefined;
};

export const normalizeTriggerEffect = (
  event: AnnotationTriggerEvent,
  value: unknown,
): AnnotationTriggerEffect | undefined => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const candidate = value as Record<string, unknown>;
  const type =
    candidate.type === 'animation' ||
    candidate.type === 'sound' ||
    candidate.type === 'emote' ||
    candidate.type === 'event' ||
    candidate.type === 'state_change'
      ? candidate.type
      : null;
  if (!type || !isTriggerEffectCompatible(event, type)) {
    return undefined;
  }

  if (type === 'animation') {
    return normalizeTriggerAnimationEffect(candidate.payload);
  }
  if (type === 'sound') {
    return normalizeTriggerSoundEffect(candidate.payload);
  }
  if (type === 'emote') {
    return normalizeTriggerEmoteEffect(candidate.payload);
  }
  if (type === 'event') {
    return normalizeTriggerEventEffect(candidate.payload);
  }
  return normalizeTriggerStateChangeEffect(candidate.payload);
};
